/**
 * Event show — a single domain event on the shared Inspector grammar
 * (design-system §5 / frontend-entity-coverage §2.3). The Event IS the audit
 * atom, so its Audit section is its own provenance (correlation/causation +
 * the generic timeline), not a separate feed.
 *
 * Primary action: **Replay** (POST /api/events/:id/replay) — re-runs the stored
 * event through the kernel (`11 §3.1`); disabled when `status === non_replayable`.
 * Overview lays out type/source/subject/correlation; Body shows the payload;
 * Relations link the subject artifact + correlation/causation siblings.
 */
import { useShow, useNavigation, type HttpError } from "@refinedev/core";
import { FileBox, GitBranch, Link2, Repeat } from "@/components/ui/icon";
import type { Event } from "@aos/shared";

import { Button } from "@/components/ui/button";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  Field,
  FieldGrid,
  JsonOrText,
  RefLink,
  RelationsBlock,
  AuditBlock,
  StatusBadge,
  type RelationGroup,
  TechnicalId,
} from "@/components/inspector";
import { ShowPageSkeleton, ShowPageError } from "@/components/inspector/ShowPage";
import { formatDateTime, orDash } from "../_shared/runs-ui";
import { useApiAction } from "../_shared/useApiAction";

export function EventShow() {
  const { query } = useShow<Event, HttpError>();
  const { list } = useNavigation();
  const { run, loading } = useApiAction();

  const record = query.data?.data;

  if (query.isLoading) return <ShowPageSkeleton />;
  if (query.isError || !record) {
    return (
      <ShowPageError
        message={query.error?.message ?? "Event not found."}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const canReplay = record.status !== "non_replayable";

  const replay = () => {
    void run(`/events/${record.id}/replay`, {
      invalidateResources: ["events", "rule-runs", "agent-runs"],
      successMessage: "Event re-emitted through the kernel.",
    });
  };

  const correlationItems = [
    {
      label: "Correlation id",
      icon: Link2,
      node: record.correlation_id ? (
        <span className="font-mono text-xs">{record.correlation_id}</span>
      ) : undefined,
    },
    {
      label: "Caused by",
      icon: GitBranch,
      resource: "events",
      id: record.causation_id,
    },
  ].filter((i) => i.node !== undefined || i.id);

  const relations: RelationGroup[] = [
    {
      title: "References",
      items: [
        {
          label: "Subject artifact",
          icon: FileBox,
          resource: "artifacts",
          id: record.artifact_id,
        },
      ],
    },
    {
      title: "Correlation",
      emptyMessage: "No correlated events.",
      items: correlationItems,
    },
  ];

  return (
    <Inspector>
      <InspectorHeader
        title={record.event_type}
        subtitle="An append-only kernel event — its type, payload, and causal chain."
        status={record.status}
        primaryAction={
          <Button
            size="sm"
            onClick={replay}
            disabled={!canReplay || loading}
            data-testid="event-replay"
          >
            <Repeat className="mr-2 h-4 w-4" />
            {loading ? "Replaying…" : "Replay"}
          </Button>
        }
        actions={
          <Button variant="ghost" size="sm" onClick={() => list("events")}>
            Back
          </Button>
        }
      />

      {!canReplay && (
        <p className="text-sm text-muted-foreground">
          This event is marked{" "}
          <span className="font-medium text-foreground">non_replayable</span>{" "}
          and cannot be re-emitted.
        </p>
      )}

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Event type">{record.event_type}</Field>
          <Field label="Source">{orDash(record.source)}</Field>
          <Field label="Subject artifact">
            <RefLink resource="artifacts" id={record.artifact_id} />
          </Field>
          <Field label="Status">
            <StatusBadge status={record.status} />
          </Field>
          <Field label="Created">{formatDateTime(record.created_at)}</Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Body">
        <JsonOrText value={record.payload} />
      </InspectorSection>

      <InspectorSection title="Relations">
        <RelationsBlock groups={relations} />
      </InspectorSection>

      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <TechnicalId id={String(record.id)} />
          </Field>
          <Field label="Correlation id">
            <span className="font-mono text-xs">
              {orDash(record.correlation_id)}
            </span>
          </Field>
          <Field label="Causation id">
            <span className="font-mono text-xs">
              {orDash(record.causation_id)}
            </span>
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Audit">
        <AuditBlock
          entityType="events"
          entityId={record.id}
          provenance={[
            {
              label: "Correlation",
              resource: "events",
              id: record.correlation_id,
            },
            { label: "Caused by", resource: "events", id: record.causation_id },
          ]}
        />
      </InspectorSection>
    </Inspector>
  );
}

export default EventShow;
